import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { FileText, Download, Filter, TrendingUp, TrendingDown, Building2, CreditCard, Users, BarChart2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend,
} from 'recharts';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
const fmtMes = (m) => { // "2026-06" → "Jun/26"
  if (!m) return m;
  const [y, mo] = m.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(mo, 10) - 1]}/${y.slice(2)}`;
};

const STATUS_BADGE = {
  pago:      'bg-green-100 text-green-700',
  em_aberto: 'bg-yellow-100 text-yellow-700',
  vencido:   'bg-red-100 text-red-700',
  cancelado: 'bg-slate-100 text-slate-500',
};
const STATUS_LABEL = { pago: 'Pago', em_aberto: 'Em Aberto', vencido: 'Vencido', cancelado: 'Cancelado' };

const Relatorios = () => {
  const [abaAtiva, setAbaAtiva] = useState('financeiros');

  // ── Financeiros ──────────────────────────────────────────────────────────────
  const [relatorio, setRelatorio] = useState([]);
  const [loadingFin, setLoadingFin] = useState(false);
  const [filtrosFin, setFiltrosFin] = useState({ tipo: '', status: '', banco: '', data_inicio: '', data_fim: '' });

  const gerarFinanceiros = useCallback(async (f = filtrosFin) => {
    setLoadingFin(true);
    try {
      const p = new URLSearchParams();
      if (f.tipo) p.append('tipo', f.tipo);
      if (f.status) p.append('status', f.status);
      if (f.banco) p.append('banco', f.banco);
      if (f.data_inicio) p.append('data_inicio', f.data_inicio);
      if (f.data_fim) p.append('data_fim', f.data_fim);
      const { data } = await api.get(`/relatorios/financeiros?${p}`);
      setRelatorio(data);
    } catch { toast.error('Erro ao gerar relatório'); }
    finally { setLoadingFin(false); }
  }, [filtrosFin]);

  // ── Acertos ───────────────────────────────────────────────────────────────────
  const [acertosData, setAcertosData] = useState(null);
  const [loadingAc, setLoadingAc] = useState(false);
  const [filtrosAc, setFiltrosAc] = useState({ data_inicio: '', data_fim: '', motorista: '' });

  const gerarAcertos = useCallback(async (f = filtrosAc) => {
    setLoadingAc(true);
    try {
      const p = new URLSearchParams();
      if (f.data_inicio) p.append('data_inicio', f.data_inicio);
      if (f.data_fim) p.append('data_fim', f.data_fim);
      if (f.motorista) p.append('motorista', f.motorista);
      const { data } = await api.get(`/relatorios/acertos?${p}`);
      setAcertosData(data);
    } catch { toast.error('Erro ao gerar relatório de acertos'); }
    finally { setLoadingAc(false); }
  }, [filtrosAc]);

  // carrega automaticamente ao abrir
  useEffect(() => { gerarFinanceiros(); }, []); // eslint-disable-line
  useEffect(() => { if (abaAtiva === 'acertos' && !acertosData) gerarAcertos(); }, [abaAtiva]); // eslint-disable-line

  // ── CSV ───────────────────────────────────────────────────────────────────────
  const exportCSV = async () => {
    try {
      const p = new URLSearchParams();
      if (filtrosFin.tipo) p.append('tipo', filtrosFin.tipo);
      if (filtrosFin.status) p.append('status', filtrosFin.status);
      if (filtrosFin.banco) p.append('banco', filtrosFin.banco);
      if (filtrosFin.data_inicio) p.append('data_inicio', filtrosFin.data_inicio);
      if (filtrosFin.data_fim) p.append('data_fim', filtrosFin.data_fim);
      const { data: blob } = await api.get(`/relatorios/export/csv?${p}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const hoje = new Date().toISOString().slice(0, 10).split('-').reverse().join('');
      a.download = `Relatorios_BoraCar_${hoje}.csv`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
      toast.success('CSV exportado!');
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Nenhum dado encontrado' : 'Erro ao exportar');
    }
  };

  // ── Totais financeiros ────────────────────────────────────────────────────────
  const totalValor   = relatorio.reduce((s, r) => s + (r.valor_liquido || 0), 0);
  const totalPago    = relatorio.filter(r => r.status === 'pago').reduce((s, r) => s + (r.valor_liquido || 0), 0);
  const totalAberto  = relatorio.filter(r => r.status === 'em_aberto').reduce((s, r) => s + (r.valor_liquido || 0), 0);
  const totalVencido = relatorio.filter(r => r.status === 'vencido').reduce((s, r) => s + (r.valor_liquido || 0), 0);

  const porBanco = relatorio.reduce((acc, r) => {
    const b = r.banco || 'Não informado';
    if (!acc[b]) acc[b] = { total: 0, count: 0 };
    acc[b].total += r.valor_liquido || 0; acc[b].count++;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-slide-up">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-1">Relatórios</h1>
        <p className="text-slate-500 text-sm">Análise financeira e de acertos de motoristas</p>
      </div>

      <Tabs value={abaAtiva} onValueChange={setAbaAtiva}>
        <TabsList>
          <TabsTrigger value="financeiros" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Financeiros
          </TabsTrigger>
          <TabsTrigger value="acertos" className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Acertos de Motoristas
          </TabsTrigger>
        </TabsList>

        {/* ── ABA FINANCEIROS ─────────────────────────────────────────────────── */}
        <TabsContent value="financeiros" className="space-y-5 mt-4">
          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <form onSubmit={e => { e.preventDefault(); gerarFinanceiros(); }} className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Tipo</label>
                  <select value={filtrosFin.tipo} onChange={e => setFiltrosFin(f => ({ ...f, tipo: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Todos</option>
                    <option value="pagar">A Pagar</option>
                    <option value="receber">A Receber</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Status</label>
                  <select value={filtrosFin.status} onChange={e => setFiltrosFin(f => ({ ...f, status: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Todos</option>
                    <option value="em_aberto">Em Aberto</option>
                    <option value="pago">Pago</option>
                    <option value="vencido">Vencido</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Banco</label>
                  <select value={filtrosFin.banco} onChange={e => setFiltrosFin(f => ({ ...f, banco: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Todos</option>
                    <option>BB BoraMais</option>
                    <option>BB BoraCar</option>
                    <option>SICOOB</option>
                    <option>Caixa Local</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Data início</label>
                  <input type="date" value={filtrosFin.data_inicio} onChange={e => setFiltrosFin(f => ({ ...f, data_inicio: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Data fim</label>
                  <input type="date" value={filtrosFin.data_fim} onChange={e => setFiltrosFin(f => ({ ...f, data_fim: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <button type="submit" disabled={loadingFin}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-60">
                  {loadingFin ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Filtrar
                </button>
                <button type="button" onClick={exportCSV}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-md hover:bg-slate-50">
                  <Download className="w-4 h-4" /> Exportar CSV
                </button>
              </form>
            </CardContent>
          </Card>

          {/* Cards resumo */}
          {relatorio.length > 0 && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total Geral</p>
                    <p className="text-xl font-bold text-blue-600">{fmt(totalValor)}</p>
                    <p className="text-xs text-slate-400 mt-1">{relatorio.length} lançamentos</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Pago</p>
                    <p className="text-xl font-bold text-green-600">{fmt(totalPago)}</p>
                    <p className="text-xs text-slate-400 mt-1">{relatorio.filter(r => r.status === 'pago').length} itens</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Em Aberto</p>
                    <p className="text-xl font-bold text-yellow-600">{fmt(totalAberto)}</p>
                    <p className="text-xs text-slate-400 mt-1">{relatorio.filter(r => r.status === 'em_aberto').length} itens</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Vencido</p>
                    <p className="text-xl font-bold text-red-600">{fmt(totalVencido)}</p>
                    <p className="text-xs text-slate-400 mt-1">{relatorio.filter(r => r.status === 'vencido').length} itens</p>
                  </CardContent>
                </Card>
              </div>

              {/* Por banco */}
              {Object.keys(porBanco).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-red-600" /> Por Banco</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(porBanco).map(([banco, d]) => (
                        <div key={banco} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-xs font-semibold text-slate-700">{banco}</p>
                            <p className="text-xs text-slate-400">{d.count} itens</p>
                          </div>
                          <p className="text-sm font-bold text-red-600">{fmt(d.total)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tabela */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Lançamentos ({relatorio.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          {['Título','Tipo','Banco','Forma Pgto','Vencimento','Valor','Status'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {relatorio.map(item => (
                          <tr key={item.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-800">{item.titulo}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${item.tipo === 'pagar' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {item.tipo === 'pagar' ? 'A Pagar' : 'A Receber'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600 text-xs">{item.banco || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 text-xs">{item.forma_pagamento || '—'}</td>
                            <td className="px-4 py-3 text-slate-600">{fmtDate(item.vencimento)}</td>
                            <td className="px-4 py-3 font-bold text-slate-800">{fmt(item.valor_liquido)}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[item.status] || 'bg-slate-100 text-slate-600'}`}>
                                {STATUS_LABEL[item.status] || item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {relatorio.length === 0 && !loadingFin && (
            <div className="text-center py-16 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum lançamento encontrado com os filtros selecionados</p>
            </div>
          )}
        </TabsContent>

        {/* ── ABA ACERTOS ─────────────────────────────────────────────────────── */}
        <TabsContent value="acertos" className="space-y-5 mt-4">
          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <form onSubmit={e => { e.preventDefault(); gerarAcertos(); }} className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1 min-w-[160px]">
                  <label className="text-xs font-medium text-slate-500">Motorista</label>
                  <input value={filtrosAc.motorista} onChange={e => setFiltrosAc(f => ({ ...f, motorista: e.target.value }))}
                    placeholder="Buscar motorista..."
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Data início</label>
                  <input type="date" value={filtrosAc.data_inicio} onChange={e => setFiltrosAc(f => ({ ...f, data_inicio: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-500">Data fim</label>
                  <input type="date" value={filtrosAc.data_fim} onChange={e => setFiltrosAc(f => ({ ...f, data_fim: e.target.value }))}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <button type="submit" disabled={loadingAc}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-60">
                  {loadingAc ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Filtrar
                </button>
              </form>
            </CardContent>
          </Card>

          {loadingAc && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
            </div>
          )}

          {acertosData && !loadingAc && (
            <>
              {/* Cards totais */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total Geral</p>
                    <p className="text-xl font-bold text-purple-600">{fmt(acertosData.totais.total_geral)}</p>
                    <p className="text-xs text-slate-400 mt-1">{acertosData.totais.acertos} acertos</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total Motoristas</p>
                    <p className="text-xl font-bold text-blue-600">{fmt(acertosData.totais.total_motoristas)}</p>
                    <p className="text-xs text-slate-400 mt-1">{acertosData.totais.motoristas} motoristas</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Total Empresa</p>
                    <p className="text-xl font-bold text-green-600">{fmt(acertosData.totais.total_empresa)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100">
                  <CardContent className="pt-5 pb-4 text-center">
                    <p className="text-xs text-slate-500 mb-1">Média por Acerto</p>
                    <p className="text-xl font-bold text-orange-600">
                      {fmt(acertosData.totais.acertos ? acertosData.totais.total_geral / acertosData.totais.acertos : 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Evolução mensal */}
              {acertosData.evolucao_mensal?.length > 1 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart2 className="w-4 h-4 text-purple-600" /> Evolução Mensal
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={acertosData.evolucao_mensal.map(m => ({ ...m, mes: fmtMes(m.mes) }))}
                        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Legend />
                        <Line type="monotone" dataKey="total_geral" name="Total Geral" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="total_motoristas" name="Motoristas" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="total_empresa" name="Empresa" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Ranking motoristas */}
              {acertosData.ranking?.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" /> Ranking por Motorista
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={Math.max(180, acertosData.ranking.length * 42)}>
                        <BarChart data={acertosData.ranking.slice(0, 8)} layout="vertical"
                          margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
                          <XAxis type="number" tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="motorista" tick={{ fontSize: 11 }} width={90} />
                          <Tooltip formatter={v => fmt(v)} />
                          <Bar dataKey="total_geral" name="Total Geral" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-600" /> BoraMais vs BoraCar
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={Math.max(180, acertosData.ranking.length * 42)}>
                        <BarChart data={acertosData.ranking.slice(0, 8)} layout="vertical"
                          margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                          <XAxis type="number" tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="motorista" tick={{ fontSize: 11 }} width={90} />
                          <Tooltip formatter={v => fmt(v)} />
                          <Legend />
                          <Bar dataKey="boramais" name="BoraMais" fill="#16a34a" radius={[0, 2, 2, 0]} />
                          <Bar dataKey="boracar" name="BoraCar" fill="#dc2626" radius={[0, 2, 2, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tabela detalhada */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Detalhamento por Motorista</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          {['#','Motorista','Acertos','Pendentes','Total Geral','Motorista','Empresa','BoraMais','BoraCar'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {acertosData.ranking.map((m, i) => (
                          <tr key={m.motorista} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-slate-400 font-mono text-xs">{i + 1}º</td>
                            <td className="px-4 py-3 font-semibold text-slate-800">{m.motorista}</td>
                            <td className="px-4 py-3 text-slate-600">{m.acertos}</td>
                            <td className="px-4 py-3">
                              {m.pendentes > 0
                                ? <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">{m.pendentes}</span>
                                : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-3 font-bold text-purple-700">{fmt(m.total_geral)}</td>
                            <td className="px-4 py-3 font-semibold text-blue-600">{fmt(m.total_motorista)}</td>
                            <td className="px-4 py-3 font-semibold text-green-600">{fmt(m.total_empresa)}</td>
                            <td className="px-4 py-3 text-slate-600">{fmt(m.boramais)}</td>
                            <td className="px-4 py-3 text-slate-600">{fmt(m.boracar)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {acertosData?.ranking?.length === 0 && !loadingAc && (
            <div className="text-center py-16 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum acerto encontrado para o período selecionado</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Relatorios;
